import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Users, Search, Plus, ArrowLeft, UserPlus } from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/Table";

const AddUsersToSession = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    setTitle("Add Users to Session");
    setDescription("Select users to add to the tutoring session");
    setActions(
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/admin/sessions")}
        className="flex items-center gap-1"
      >
        <ArrowLeft size={16} />
        Back to Sessions
      </Button>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  // Mock session data
  const session = {
    id: "123",
    subject: "Mathematics",
    date: "2023-12-15",
    time: "14:00",
    tutor: "John Smith",
  };

  // Mock users data
  const users = [
    {
      id: "1",
      name: "Alice Johnson",
      email: "alice@example.com",
      role: "Student",
    },
    {
      id: "2",
      name: "Bob Williams",
      email: "bob@example.com",
      role: "Student",
    },
    {
      id: "3",
      name: "Carol Davis",
      email: "carol@example.com",
      role: "Student",
    },
    {
      id: "4",
      name: "Dave Wilson",
      email: "dave@example.com",
      role: "Student",
    },
  ];

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSubmit = () => {
    console.log("Adding users to session:", selectedUsers);
    navigate("/admin/sessions");
  };

  return (
    <>
      <div className="space-y-6">
        <Card>
            <CardHeader>
              <CardTitle>Session Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Subject</p>
                  <p>{session.subject}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Tutor</p>
                  <p>{session.tutor}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p>{session.date}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p>{session.time}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="mr-2 h-5 w-5 text-rfpurple-600" />
                Select Users to Add
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <Input
                  placeholder="Search users by name or email"
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Select</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-4 w-4 text-rfpurple-600 focus:ring-rfpurple-500 border-gray-300 rounded"
                        />
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          <CardFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/sessions")}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex items-center gap-2"
              disabled={selectedUsers.length === 0}
            >
              <UserPlus size={16} />
              Add Selected Users
            </Button>
          </CardFooter>
        </Card>
      </div>
    </>
  );
};

export default AddUsersToSession;
